import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  MapPin, Search, Sparkles, Building2, ShieldCheck, ArrowRight, RefreshCw,
  Layers, Map as MapIcon, Grid, TrendingUp, Tag, Calendar, CheckCircle2, ChevronRight,
  ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import NavbarHeader from "@/components/NavbarHeader";
import { stripMarkdownAndSanitize } from "@/components/RichWriteup";
import { API } from "@/lib/api";
import { useSgdChfRate, formatPriceFrom, formatPsf } from "@/lib/currency";

const BASE_URL = API.replace(/\/api$/, "");

function cleanDriveUrl(url, isThumb = true, size = 600) {
  if (!url) return "";
  const s = String(url).trim();
  if (s.startsWith("/api/launches/media//") || s.includes("/upload/broke/")) {
    return "";
  }
  const param = isThumb ? `?thumb=true&size=${size}` : "";
  if (s.startsWith("/api/")) {
    const hasThumb = s.includes("thumb=true");
    if (isThumb && !hasThumb) {
      return `${BASE_URL}${s}${s.includes("?") ? `&thumb=true&size=${size}` : `?thumb=true&size=${size}`}`;
    }
    return `${BASE_URL}${s}`;
  }
  if (s.includes("drive.google.com") || s.includes("docs.google.com")) {
    const match = s.match(/(?:id=|\/d\/|\/file\/d\/)([a-zA-Z0-9_-]{25,})/);
    if (match) return `${API}/launches/media/${match[1]}${param}`;
  }
  if (s.length >= 25 && !s.startsWith("http") && !s.includes("/")) {
    return `${API}/launches/media/${s}${param}`;
  }
  return s;
}

function isResidentialProject(p) {
  if (!p) return false;
  const propType = String(p.propertyType || "").toLowerCase();

  // If explicitly residential, condo, apartment, or mixed residential development -> keep
  if (
    propType.includes("residential") ||
    propType.includes("condo") ||
    propType.includes("apartment") ||
    propType.includes("mixed") ||
    propType.includes("flat") ||
    propType.includes("tower") ||
    propType.includes("block")
  ) {
    return true;
  }

  // Pure non-residential property types to exclude
  const strictNonRes = [
    "business space", "industrial", "office", "retail", "shophouse",
    "factory", "warehouse", "b1", "b2", "gate+", "canteen", "business park",
    "food factory", "medical clinic", "clinic", "ramp up"
  ];

  for (const kw of strictNonRes) {
    if (propType === kw || propType.startsWith(kw) || (propType.includes(kw) && !propType.includes("resi"))) {
      return false;
    }
  }

  return true;
}

function normalizeTenure(raw) {
  if (!raw) return "99-year Leasehold";
  const t = String(raw).toLowerCase().trim();
  if (t.includes("freehold") || t.includes("fee simple") || t.includes("999")) return "Freehold / 999-year";
  return "99-year Leasehold";
}

function formatTenureWithYear(raw) {
  if (!raw) return "99-year Leasehold";
  const s = String(raw).trim();
  const norm = normalizeTenure(s);
  if (norm === "Freehold / 999-year") return "Freehold / 999-year";
  const match = s.match(/\b(19\d\d|20\d\d)\b/);
  if (match) return `${norm} from ${match[1]}`;
  return norm;
}

function AvailBadge({ status }) {
  const map = {
    available: { bg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400", dot: "bg-emerald-400", label: "Units Available" },
    limited: { bg: "bg-amber-500/10 border-amber-500/30 text-amber-400", dot: "bg-amber-400", label: "Limited Units Left" },
    upcoming: { bg: "bg-blue-500/10 border-blue-500/30 text-blue-400", dot: "bg-blue-400", label: "Upcoming Launch" },
    "sold-out": { bg: "bg-rose-500/10 border-rose-500/30 text-rose-400", dot: "bg-rose-400", label: "Sold Out" },
  };
  const s = map[status] || map.available;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${s.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </div>
  );
}

const DISTRICT_NAME_MAP = {
  D01: "Raffles Place / Marina Bay / Boat Quay",
  D02: "Anson / Tanjong Pagar / Chinatown",
  D03: "Queenstown / Tiong Bahru / Alexandra",
  D04: "Sentosa / Telok Blangah / HarbourFront",
  D05: "Buona Vista / West Coast / Clementi",
  D06: "City Hall / Clarke Quay",
  D07: "Middle Road / Golden Mile / Bugis",
  D08: "Little India / Farrer Park",
  D09: "Orchard / Cairnhill / River Valley",
  D10: "Tanglin / Holland / Nassim / Bukit Timah",
  D11: "Newton / Novena / Dunearn",
  D12: "Toa Payoh / Balestier / Serangoon",
  D13: "MacPherson / Potong Pasir / Braddell",
  D14: "Eunos / Geylang / Paya Lebar",
  D15: "East Coast / Marine Parade / Katong",
  D16: "Bedok / Upper East Coast / Bayshore",
  D17: "Changi / Loyang",
  D18: "Tampines / Pasir Ris",
  D19: "Serangoon / Hougang / Sengkang / Punggol",
  D20: "Bishan / Ang Mo Kio / Thomson",
  D21: "Upper Bukit Timah / Clementi Park",
  D22: "Boon Lay / Jurong / Tuas",
  D23: "Bukit Batok / Hillview / Choa Chu Kang",
  D24: "Lim Chu Kang / Tengah",
  D25: "Woodlands / Kranji",
  D26: "Mandai / Upper Thomson",
  D27: "Yishun / Sembawang",
  D28: "Seletar / Yio Chu Kang",
};

export function formatDistrictLabel(raw) {
  if (!raw) return "Singapore";
  const s = String(raw).trim();
  const match = s.match(/D?0*(\d+)/i);
  if (match) {
    const num = parseInt(match[1], 10);
    const code = `D${num < 10 ? "0" + num : num}`;
    const name = DISTRICT_NAME_MAP[code];
    if (name) {
      if (s.includes(" - ")) return s;
      return `${code} - ${name}`;
    }
    return s.startsWith("D") ? s : `D${num < 10 ? "0" + num : num}`;
  }
  return s;
}

const DISTRICT_ZONES = [
  {
    region: "Core Central Region (CCR)",
    color: "border-[#C5A880]/50 bg-[#C5A880]/10 text-[#C5A880]",
    districts: [
      { id: "D01", name: "Raffles Place / Marina Bay / Boat Quay" },
      { id: "D02", name: "Anson / Tanjong Pagar / Chinatown" },
      { id: "D04", name: "Sentosa / Telok Blangah / HarbourFront" },
      { id: "D06", name: "City Hall / Clarke Quay" },
      { id: "D09", name: "Orchard / Cairnhill / River Valley" },
      { id: "D10", name: "Tanglin / Holland / Nassim / Bukit Timah" },
      { id: "D11", name: "Newton / Novena / Dunearn" },
    ],
  },
  {
    region: "Rest of Central Region (RCR)",
    color: "border-blue-500/40 bg-blue-500/10 text-blue-300",
    districts: [
      { id: "D03", name: "Queenstown / Tiong Bahru / Alexandra" },
      { id: "D05", name: "Buona Vista / West Coast / Clementi" },
      { id: "D07", name: "Middle Road / Golden Mile / Bugis" },
      { id: "D08", name: "Little India / Farrer Park" },
      { id: "D12", name: "Toa Payoh / Balestier / Serangoon" },
      { id: "D13", name: "MacPherson / Potong Pasir / Braddell" },
      { id: "D14", name: "Eunos / Geylang / Paya Lebar" },
      { id: "D15", name: "East Coast / Marine Parade / Katong" },
    ],
  },
  {
    region: "Outside Central Region (OCR)",
    color: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    districts: [
      { id: "D16", name: "Bedok / Upper East Coast / Bayshore" },
      { id: "D17", name: "Changi / Loyang" },
      { id: "D18", name: "Tampines / Pasir Ris" },
      { id: "D19", name: "Serangoon / Hougang / Sengkang / Punggol" },
      { id: "D20", name: "Bishan / Ang Mo Kio / Thomson" },
      { id: "D21", name: "Upper Bukit Timah / Clementi Park" },
      { id: "D22", name: "Boon Lay / Jurong / Tuas" },
      { id: "D23", name: "Bukit Batok / Hillview / Choa Chu Kang" },
      { id: "D24", name: "Lim Chu Kang / Tengah" },
      { id: "D25", name: "Woodlands / Kranji" },
      { id: "D26", name: "Mandai / Upper Thomson" },
      { id: "D27", name: "Yishun / Sembawang" },
      { id: "D28", name: "Seletar / Yio Chu Kang" },
    ],
  },
];

function getUnitCountLabel(u) {
  if (u.units != null && u.units !== "") {
    const n = Number(u.units);
    if (!Number.isNaN(n)) {
      return `${n} ${n === 1 ? "unit" : "units"}`;
    }
    return String(u.units);
  }
  if (u.availability) {
    const m = String(u.availability).match(/\d+/);
    if (m) {
      const n = Number(m[0]);
      return `${n} ${n === 1 ? "unit" : "units"}`;
    }
  }
  return null;
}

function LaunchCard({ launch, chfRate, idx }) {
  const [showAllUnits, setShowAllUnits] = useState(false);
  const heroImg =
    cleanDriveUrl(launch.heroImageUrl, true, 600) ||
    "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect width='800' height='600' fill='%230f172a'/%3E%3Cg fill='none' stroke='%23334155' stroke-width='3'%3E%3Crect x='300' y='180' width='200' height='260' rx='8'/%3E%3Cpath d='M340 220 h30 v30 h-30 z M430 220 h30 v30 h-30 z M340 280 h30 v30 h-30 z M430 280 h30 v30 h-30 z M340 340 h30 v30 h-30 z M430 340 h30 v30 h-30 z M370 440 v-40 h60 v40'/%3E%3C/g%3E%3Ctext x='50%25' y='80%25' dominant-baseline='middle' text-anchor='middle' fill='%2364748b' font-family='sans-serif' font-size='16'%3EArchitectural Render Unavailable%3C/text%3E%3C/svg%3E";
  const slugStr = launch.slug?.current || launch.slug || launch._id;
  const unitMix = Array.isArray(launch.unitMix) ? launch.unitMix : [];
  const hasManyUnits = unitMix.length > 4;
  const visibleUnits = hasManyUnits && !showAllUnits ? unitMix.slice(0, 3) : unitMix;

  return (
    <div className="bg-white border border-[#E7E2D6] rounded-2xl overflow-hidden hover:border-[#C5A880] hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
      <div>
        {/* Image Banner */}
        <Link to={`/launches/${slugStr}`} className="block relative h-52 overflow-hidden bg-slate-100">
          <img
            src={heroImg}
            alt={launch.title}
            loading={idx < 3 ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={idx < 3 ? "high" : "auto"}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect width='800' height='600' fill='%230f172a'/%3E%3Cg fill='none' stroke='%23334155' stroke-width='3'%3E%3Crect x='300' y='180' width='200' height='260' rx='8'/%3E%3Cpath d='M340 220 h30 v30 h-30 z M430 220 h30 v30 h-30 z M340 280 h30 v30 h-30 z M430 280 h30 v30 h-30 z M340 340 h30 v30 h-30 z M430 340 h30 v30 h-30 z M370 440 v-40 h60 v40'/%3E%3C/g%3E%3Ctext x='50%25' y='80%25' dominant-baseline='middle' text-anchor='middle' fill='%2364748b' font-family='sans-serif' font-size='16'%3EArchitectural Render Unavailable%3C/text%3E%3C/svg%3E";
            }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10">
            <span className="bg-[#0A1128]/90 text-[#C5A880] border border-[#C5A880]/30 px-2.5 py-0.5 rounded text-[11px] font-semibold backdrop-blur">
              {formatDistrictLabel(launch.district)}
            </span>
            <span className="bg-black/60 text-slate-200 px-2 py-0.5 rounded text-[11px] backdrop-blur font-medium">
              {formatTenureWithYear(launch.tenure || launch.rawTenure)}
            </span>
            {(launch.isPromotion || launch.promotionLabel || launch.isDiscounted) && (
              <span className="bg-[#C5A880] text-[#0A1128] font-bold px-2 py-0.5 rounded text-[11px] shadow">
                Promotion
              </span>
            )}
          </div>

          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
            <div className="text-[#C5A880] font-serif-display text-xl font-semibold drop-shadow">
              {formatPriceFrom(launch.priceFrom, chfRate)}
            </div>
            {launch.psfFrom && (
              <div className="text-[11px] text-slate-200 bg-black/60 px-2 py-0.5 rounded backdrop-blur font-medium">
                {formatPsf(launch.psfFrom, chfRate)}
              </div>
            )}
          </div>
        </Link>

        {/* Card Body */}
        <div className="p-5 space-y-3.5">
          {/* Title & Location */}
          <div>
            <Link to={`/launches/${slugStr}`}>
              <h3 className="font-serif-display text-xl font-medium text-[#0A1128] group-hover:text-[#C5A880] transition-colors leading-snug">
                {launch.title}
              </h3>
            </Link>
            <p className="text-xs text-slate-600 mt-1 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#C5A880] shrink-0" />
              <span className="line-clamp-1">{launch.location}</span>
            </p>
            {launch.description && (
              <p className="text-xs text-slate-500 mt-2 line-clamp-2 leading-relaxed">
                {stripMarkdownAndSanitize(launch.description)}
              </p>
            )}
          </div>

          {/* Quick Specs (TOP & Total Available) */}
          <div className="grid grid-cols-2 gap-2 text-[11px] bg-[#F9FAFB] p-2.5 rounded-xl border border-[#E2E8F0]">
            <div>
              <span className="text-slate-500 block">TOP Date</span>
              <span className="text-[#0A1128] font-medium">{launch.topDate || "TBC"}</span>
            </div>
            <div>
              <span className="text-slate-500 block">Available Units</span>
              <span className="text-[#0A1128] font-medium">
                {launch.availableUnits
                  ? `${launch.availableUnits} units available`
                  : launch.unitsRemaining
                  ? `${launch.unitsRemaining} units available`
                  : launch.totalUnits
                  ? `${launch.totalUnits} units available`
                  : "TBC"}
              </span>
            </div>
          </div>

          {/* Available Unit Types and Starting Prices */}
          {unitMix.length > 0 && (
            <div className="space-y-1.5 pt-0.5">
              <div className="flex items-center justify-between text-[10px] font-bold text-slate-500 uppercase tracking-wider px-0.5">
                <span>Available Unit Types</span>
                <span>Starting Price</span>
              </div>
              <div className="bg-[#F9FAFB] rounded-xl border border-[#E2E8F0] divide-y divide-[#E2E8F0]/70 overflow-hidden text-xs">
                {visibleUnits.map((u, uIdx) => {
                  const countLabel = getUnitCountLabel(u);
                  return (
                    <Link
                      key={uIdx}
                      to={`/launches/${slugStr}`}
                      className="flex items-center justify-between px-3 py-1.5 hover:bg-[#F5F3EE] transition-colors group/row"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-semibold text-[#0A1128] text-xs truncate">
                          {u.type}
                        </span>
                        {countLabel && (
                          <span className="text-[10px] text-slate-500 font-normal shrink-0">
                            ({countLabel})
                          </span>
                        )}
                      </div>
                      <div className="font-semibold text-xs text-[#0A1128] group-hover/row:text-[#C5A880] transition-colors text-right shrink-0 ml-2">
                        {formatPriceFrom(u.priceFrom, chfRate)}
                      </div>
                    </Link>
                  );
                })}

                {hasManyUnits && !showAllUnits && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAllUnits(true);
                    }}
                    className="w-full py-1.5 px-3 text-[11px] font-semibold text-[#C5A880] hover:text-[#0A1128] bg-[#F5F3EE]/60 hover:bg-[#F5F3EE] flex items-center justify-center gap-1 transition-colors"
                  >
                    + {unitMix.length - 3} more unit types (Show all)
                    <ChevronDown className="w-3 h-3 ml-0.5" />
                  </button>
                )}
                {hasManyUnits && showAllUnits && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowAllUnits(false);
                    }}
                    className="w-full py-1 px-3 text-[10px] text-slate-500 hover:text-slate-700 bg-slate-100/50 flex items-center justify-center gap-1 transition-colors"
                  >
                    Show less
                    <ChevronUp className="w-3 h-3 ml-0.5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Action */}
      <div className="p-5 pt-3 border-t border-slate-200 flex items-center justify-between mt-auto">
        <span
          className="text-[11px] text-slate-500 line-clamp-1 max-w-[170px] font-medium"
          title={launch.developer}
        >
          {launch.developer || "Direct Developer Inventory"}
        </span>
        <Link to={`/launches/${slugStr}`}>
          <Button
            size="sm"
            className="bg-[#0A1128] text-white hover:bg-[#152243] font-semibold text-xs rounded-lg px-3.5 shadow-sm"
          >
            View Dossier <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function Launches() {
  const chfRate = useSgdChfRate();
  const [launches, setLaunches] = useState(() => {
    try {
      const cached = localStorage.getItem("asp_launches_cache") || sessionStorage.getItem("asp_launches_cache");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_e) {}
    return [];
  });
  const [loading, setLoading] = useState(() => {
    try {
      const cached = localStorage.getItem("asp_launches_cache") || sessionStorage.getItem("asp_launches_cache");
      if (cached && JSON.parse(cached).length > 0) return false;
    } catch (_e) {}
    return true;
  });
  const [search, setSearch] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTenure, setSelectedTenure] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  const loadLaunches = async () => {
    if (launches.length === 0) setLoading(true);
    try {
      const res = await api.get("/launches");
      const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.launches) ? res.data.launches : [];
      if (list.length > 0) {
        const seen = new Set();
        const unique = list.filter((l) => {
          if (!l) return false;
          const slugKey = String(l.slug?.current || l.slug || l.title || "").toLowerCase().trim();
          if (!slugKey || seen.has(slugKey)) return false;
          seen.add(slugKey);
          return true;
        });
        setLaunches(unique);
        try {
          localStorage.setItem("asp_launches_cache", JSON.stringify(unique));
          sessionStorage.setItem("asp_launches_cache", JSON.stringify(unique));
        } catch (_e) {}
      }
    } catch (_err) {
      toast.error("Failed to load live new launch directory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLaunches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const districts = useMemo(() => {
    if (!Array.isArray(launches)) return [];
    const set = new Set(launches.filter(isResidentialProject).map((l) => l && String(l.district || "")).filter(Boolean));
    return Array.from(set).sort();
  }, [launches]);

  const tenureOptions = [
    "Freehold / 999-year",
    "99-year Leasehold"
  ];

  const districtCounts = useMemo(() => {
    const counts = {};
    if (!Array.isArray(launches)) return counts;
    launches.filter(isResidentialProject).forEach((l) => {
      if (l && l.district) {
        const key = String(l.district).toUpperCase();
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [launches]);

  const filteredLaunches = useMemo(() => {
    const seen = new Set();
    if (!Array.isArray(launches)) return [];
    return launches.filter((l) => {
      if (!l) return false;
      const slugKey = String(l.slug?.current || l.slug || l.title || "").toLowerCase().trim();
      if (!slugKey || seen.has(slugKey)) return false;

      // 0. Strict Residential filter
      if (!isResidentialProject(l)) return false;

      // 1. Search filter
      const searchStr = String(search || "").toLowerCase().trim();
      const matchSearch =
        !searchStr ||
        String(l.title || "").toLowerCase().includes(searchStr) ||
        String(l.location || "").toLowerCase().includes(searchStr) ||
        String(l.developer || "").toLowerCase().includes(searchStr);

      // 2. District filter
      const matchDistrict = !selectedDistrict || String(l.district || "").toUpperCase().includes(String(selectedDistrict).toUpperCase());

      // 3. Tenure filter (grouped)
      const normT = normalizeTenure(l.tenure);
      const matchTenure = !selectedTenure || normT === selectedTenure || String(l.tenure || "").toLowerCase().includes(String(selectedTenure).toLowerCase());

      const isPass = matchSearch && matchDistrict && matchTenure;
      if (isPass) seen.add(slugKey);
      return isPass;
    });
  }, [launches, search, selectedDistrict, selectedTenure]);

  return (
    <div className="min-h-screen bg-[#F5F3EE] text-[#0A1128]">
      {/* Alpine Straits Shared Header Navbar */}
      <NavbarHeader />

      {/* Header Banner */}
      <div className="border-b border-[#E7E2D6] bg-gradient-to-b from-[#F5F3EE] via-[#EFECE6] to-[#F5F3EE] py-10 lg:py-14 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <div className="flex items-center gap-2 text-[#C5A880] text-xs font-semibold tracking-widest uppercase">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              Live Singapore New Launch Directory
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-[#E7E2D6] shadow-sm">
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  viewMode === "grid" ? "bg-[#0A1128] text-white font-semibold shadow" : "text-slate-600 hover:text-[#0A1128]"
                }`}
              >
                <Grid className="w-3.5 h-3.5" /> Card Grid View
              </button>
              <button
                onClick={() => setViewMode("map")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  viewMode === "map" ? "bg-[#0A1128] text-white font-semibold shadow" : "text-slate-600 hover:text-[#0A1128]"
                }`}
              >
                <MapIcon className="w-3.5 h-3.5" /> Interactive Map View
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="font-serif-display text-3xl lg:text-5xl font-medium text-[#0A1128] tracking-tight">
                Singapore New Launch Condominiums
              </h1>
              <p className="text-slate-600 mt-2 text-sm max-w-2xl leading-relaxed">
                Direct developer master catalog. Synchronized live with direct developer inventory & prime residential portfolios.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={loadLaunches}
                className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-[#0A1128] shadow-sm"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {/* Search & Filter Bar */}
        <div className="bg-white border border-[#E7E2D6] rounded-xl p-4 mb-8 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative col-span-1 sm:col-span-2 lg:col-span-2">
              <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search project name or location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-[#F9FAFB] border-slate-200 focus:border-[#C5A880] pl-10 text-sm text-[#0A1128] placeholder:text-slate-400 rounded-lg"
              />
            </div>

            <div>
              <select
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                className="w-full bg-[#F9FAFB] border border-slate-200 focus:border-[#C5A880] rounded-lg px-3 py-2 text-sm text-[#0A1128] outline-none"
              >
                <option value="">All Districts ({districts.length})</option>
                {districts.map((d) => (
                  <option key={d} value={d}>
                    {formatDistrictLabel(d)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={selectedTenure}
                onChange={(e) => setSelectedTenure(e.target.value)}
                className="w-full bg-[#F9FAFB] border border-slate-200 focus:border-[#C5A880] rounded-lg px-3 py-2 text-sm text-[#0A1128] outline-none"
              >
                <option value="">All Tenures</option>
                {tenureOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedDistrict && (
            <div className="mt-3 flex items-center justify-between text-xs text-slate-600 bg-[#F9FAFB] px-3 py-1.5 rounded-lg border border-slate-200">
              <span>Filter active: District <strong className="text-[#0A1128]">{formatDistrictLabel(selectedDistrict)}</strong></span>
              <button onClick={() => setSelectedDistrict("")} className="text-[#C5A880] hover:text-[#0A1128] font-medium underline">
                Clear District Filter
              </button>
            </div>
          )}
        </div>

        {/* INTERACTIVE MAP VIEW */}
        {viewMode === "map" && (
          <div className="mb-10 bg-white border border-[#E7E2D6] rounded-2xl p-6 shadow-sm space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif-display text-xl text-[#0A1128] flex items-center gap-2">
                  <MapIcon className="w-5 h-5 text-[#C5A880]" /> Singapore Property District Interactive Map
                </h3>
                <p className="text-xs text-slate-600 mt-1">
                  Click any district node to instantly filter new launch developments in that zone.
                </p>
              </div>
              <div className="text-xs text-[#0A1128] bg-[#F5F3EE] px-3 py-1 rounded-full border border-[#E7E2D6] font-medium">
                {districts.length} Districts Available
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {DISTRICT_ZONES.map((zone, zIdx) => (
                <div key={zIdx} className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-xl p-4">
                  <div className="text-xs font-semibold text-[#0A1128] uppercase tracking-wider mb-3 flex items-center justify-between">
                    <span>{zone.region}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {zone.districts.map((d) => {
                      const isSelected = String(selectedDistrict || "").toUpperCase().includes(d.id);
                      const count = districtCounts[d.id] || 0;
                      return (
                        <button
                          key={d.id}
                          onClick={() => setSelectedDistrict(isSelected ? "" : d.id)}
                          className={`p-2.5 rounded-lg border text-left transition-all text-xs flex flex-col justify-between ${
                            isSelected
                              ? "bg-[#0A1128] text-white border-[#0A1128] font-bold shadow"
                              : "bg-white text-slate-700 border-slate-200 hover:border-[#C5A880] hover:bg-[#FDF8EF]"
                          }`}
                        >
                          <div className="flex items-center justify-between w-full font-semibold">
                            <span>{d.id}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${isSelected ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>
                              {count} {count === 1 ? "project" : "projects"}
                            </span>
                          </div>
                          <div className={`text-[10px] mt-1 line-clamp-1 ${isSelected ? "text-white/90 font-medium" : "text-slate-500"}`}>
                            {d.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results Counter */}
        <div className="flex items-center justify-between text-xs text-slate-600 mb-6">
          <div>
            Showing <span className="text-[#0A1128] font-semibold">{filteredLaunches.length}</span> of {launches.filter(isResidentialProject).length} new launch projects
          </div>
          <div className="text-slate-500">
            Prices shown in CHF &middot; 1 SGD = {chfRate.toFixed(4)} CHF
          </div>
        </div>

        {/* Loading Skeleton */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-[420px] rounded-2xl bg-white border border-[#E7E2D6]" />
            ))}
          </div>
        ) : filteredLaunches.length === 0 ? (
          <div className="bg-white border border-[#E7E2D6] rounded-2xl p-12 text-center max-w-md mx-auto my-8 shadow-sm">
            <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <h3 className="font-serif-display text-lg text-[#0A1128]">No projects found</h3>
            <p className="text-xs text-slate-600 mt-1 mb-4">No new launches match your selected district or search criteria.</p>
            <Button
              onClick={() => { setSearch(""); setSelectedDistrict(""); setSelectedTenure(""); setActiveTab("all"); }}
              className="bg-[#0A1128] text-white font-semibold text-xs hover:bg-[#152243]"
            >
              Reset All Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLaunches.map((launch, idx) => (
              <LaunchCard key={launch._id} launch={launch} chfRate={chfRate} idx={idx} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
